// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useQuery } from '@tanstack/react-query';
import { get } from '@tls/auth';
import {
  parseReleaseInformation,
  type ReleaseInformation,
  type ReleaseInformationErrorReason,
} from 'model/ReleaseInformation';

export class ReleaseInformationError extends Error {
  constructor(public readonly reason: ReleaseInformationErrorReason) {
    super(reason);
  }
}

export const releaseQuerySettings = {
  staleTime: Infinity,
  gcTime: Infinity,
  retry: (failureCount: number, error: Error) =>
    failureCount < 2 &&
    !(error instanceof ReleaseInformationError && (error.reason === 'empty' || error.reason === 'invalidData')),
  retryOnMount: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  networkMode: 'always',
} as const;

export default function useReleaseInformationApi(appBaseUrl = '', enabled = false) {
  return useQuery({
    ...releaseQuerySettings,
    queryKey: ['release-information', appBaseUrl],
    queryFn: getReleaseInformation,
    enabled,
  });
}

async function getReleaseInformation(): Promise<ReleaseInformation> {
  const response = await get('release-information');
  if (response?.status === 404) throw new ReleaseInformationError('empty');
  if (response?.status === 503) throw new ReleaseInformationError('maintenance');
  if (response?.status !== 200) throw new ReleaseInformationError('loadError');

  let data: ReleaseInformation;
  try {
    data = parseReleaseInformation(response.data);
  } catch {
    throw new ReleaseInformationError('invalidData');
  }
  if (!data.releases.length) throw new ReleaseInformationError('empty');
  return data;
}
